import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Info, Loader2, Search } from 'lucide-react';

import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getAdSetup, generateScript, updateAdSetup } from '@/services/api/ad-setup';
import { getGranulations, resolveSlugs } from '@/services/api/price-structure';
import { useToast } from '@/hooks/use-toast';

import {
    SELECAO_VAZIA, contaEscopos, daApi, paraSalvar,
    type Selection, type StructureCascateDraft, type StructureScope,
} from '../structureCascate';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    domainId: number | null;
    domainName: string;
}

/** Acima disso o ad.js começa a inchar sem o ganho ficar óbvio. */
const MUITAS_ENTRADAS = 200;

/** Uma entrada por linha: em bloco único, quinze combinações viram uma parede. */
const formataEscopos = (escopos: StructureScope[]): string =>
    escopos.length === 0
        ? '[]'
        : `[\n${escopos.map((e) => `  ${JSON.stringify(e)}`).join(',\n')}\n]`;

export function ActivationModal({ open, onOpenChange, domainId, domainName }: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [draft, setDraft] = useState<StructureCascateDraft>({
        active: false,
        mode: 'filtros',
        selection: SELECAO_VAZIA,
    });
    const [representavel, setRepresentavel] = useState(true);
    const [salvoOriginal, setSalvoOriginal] = useState<StructureScope[]>([]);
    const [tocado, setTocado] = useState(false);
    const [buscaPost, setBuscaPost] = useState('');
    const [slugs, setSlugs] = useState<Record<string, string | null>>({});

    // As opções saem do que a estrutura gerou: só faz sentido liberar onde há
    // preço calculado. Listar todo país do GAM ofereceria alvo que não existe.
    const { data: granulations, isLoading: carregandoOpcoes } = useQuery({
        queryKey: ['price-structure-granulations', domainId],
        queryFn: () => getGranulations(domainId!),
        enabled: open && domainId !== null,
    });

    const { data: setup, isLoading: carregandoSetup } = useQuery({
        queryKey: ['ad-setup', domainId],
        queryFn: () => getAdSetup(domainId!),
        enabled: open && domainId !== null,
    });

    const opcoes = useMemo(() => {
        const countries = new Set<string>();
        const posts = new Set<string>();
        const blocks = new Set<string>();
        Object.entries(granulations?.granulations ?? {}).forEach(([post, byCountry]) => {
            posts.add(post);
            Object.entries(byCountry).forEach(([country, byBlock]) => {
                countries.add(country);
                Object.keys(byBlock).forEach((block) => blocks.add(block));
            });
        });
        const ordena = (s: Set<string>) => [...s].sort((a, b) => a.localeCompare(b));
        return { countries: ordena(countries), posts: ordena(posts), blocks: ordena(blocks) };
    }, [granulations]);

    // Carrega o que está salvo assim que o modal abre, uma vez só: recarregar a
    // cada render apagaria o que está sendo marcado agora.
    useEffect(() => {
        if (!open || !setup) return;
        const vindo = daApi(setup.active_structure_cascate, setup.structure_cascate);
        setDraft({ active: vindo.active, mode: vindo.mode, selection: vindo.selection });
        setRepresentavel(vindo.representavel);
        setSalvoOriginal(vindo.original);
        setTocado(false);
        setBuscaPost('');
    }, [open, setup]);

    // Título do post em vez do id cru — o id sozinho não diz que página é.
    useEffect(() => {
        if (!open || !domainId || !opcoes.posts.length) return;
        resolveSlugs(domainId, opcoes.posts)
            .then((r) => setSlugs(r.slugs))
            .catch(() => setSlugs({}));
    }, [open, domainId, opcoes.posts]);

    const alterar = (mudanca: Partial<StructureCascateDraft>) => {
        setDraft((d) => ({ ...d, ...mudanca }));
        setTocado(true);
        setRepresentavel(true);
    };

    const alternar = (dimensao: keyof Selection, valor: string) =>
        alterar({
            selection: {
                ...draft.selection,
                [dimensao]: draft.selection[dimensao].includes(valor)
                    ? draft.selection[dimensao].filter((v) => v !== valor)
                    : [...draft.selection[dimensao], valor],
            },
        });

    const payload = paraSalvar(draft);
    const total = draft.mode === 'tudo' ? 1 : contaEscopos(draft.selection);
    const nadaMarcado = draft.mode === 'filtros' && payload.structure_cascate.length === 0;

    const salvar = useMutation({
        // Salvar sem regerar deixaria a mudança presa no banco: o script lê o
        // ad.js, não a tabela. As duas coisas juntas é o que "ativar" significa.
        mutationFn: async () => {
            await updateAdSetup(domainId!, payload);
            await generateScript(domainId!);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ad-setup', domainId] });
            toast({
                title: draft.active ? 'Estrutura ativada' : 'Estrutura desativada',
                description: `ad${domainId}.js regerado para ${domainName}.`,
            });
            onOpenChange(false);
        },
        onError: (error: any) =>
            toast({
                variant: 'destructive',
                title: 'Não foi possível salvar',
                description: error?.response?.data?.detail || 'Falha ao gravar a liberação.',
            }),
    });

    const carregando = carregandoOpcoes || carregandoSetup;

    const listaPosts = opcoes.posts.filter((post) => {
        const busca = buscaPost.trim().toLowerCase();
        if (!busca) return true;
        return post.includes(busca) || (slugs[post] ?? '').toLowerCase().includes(busca);
    });

    const Grupo = ({
        titulo,
        dimensao,
        itens,
        rotulo,
    }: {
        titulo: string;
        dimensao: keyof Selection;
        itens: string[];
        rotulo?: (v: string) => string;
    }) => {
        const marcados = draft.selection[dimensao];
        return (
            <div className="min-w-0 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {titulo}
                    </span>
                    <span className="text-[11px] text-gray-400">
                        {marcados.length
                            ? `${marcados.length} de ${itens.length}`
                            : `todos (${itens.length})`}
                    </span>
                </div>
                {dimensao === 'posts' && itens.length > 8 && (
                    <div className="relative border-b border-gray-100 p-2">
                        <Search className="absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                        <Input
                            value={buscaPost}
                            onChange={(e) => setBuscaPost(e.target.value)}
                            placeholder="Buscar post por id ou slug"
                            className="h-8 pl-8 text-xs"
                        />
                    </div>
                )}
                <div className="max-h-44 overflow-y-auto p-1">
                    {itens.length === 0 ? (
                        <p className="px-2 py-3 text-xs text-gray-400">
                            Nada aqui ainda — rode a estrutura primeiro.
                        </p>
                    ) : (
                        itens.map((item) => (
                            <label
                                key={item}
                                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-gray-50"
                            >
                                <input
                                    type="checkbox"
                                    checked={marcados.includes(item)}
                                    onChange={() => alternar(dimensao, item)}
                                    className="h-3.5 w-3.5 rounded border-gray-300"
                                />
                                <span className="truncate text-xs text-gray-800">
                                    {rotulo ? rotulo(item) : item}
                                </span>
                            </label>
                        ))
                    )}
                </div>
                {marcados.length > 0 && (
                    <button
                        type="button"
                        onClick={() => alterar({ selection: { ...draft.selection, [dimensao]: [] } })}
                        className="w-full border-t border-gray-100 py-1.5 text-[11px] text-gray-500 hover:bg-gray-50"
                    >
                        Limpar — volta a valer para todos
                    </button>
                )}
            </div>
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* `min-w-0` no conteúdo: o DialogContent é um grid, e filho de grid
                tem `min-width: auto` — o JSON do escopo, que é uma linha longa,
                esticava o modal para além do max-w e o empurrava para fora da
                tela por causa do translate de centralização. */}
            <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto overflow-x-hidden">
                <DialogHeader>
                    <DialogTitle>Liberação da estrutura · {domainName}</DialogTitle>
                    <DialogDescription>
                        Onde os preços gerados pelo motor substituem as granulações manuais.
                    </DialogDescription>
                </DialogHeader>

                {carregando ? (
                    <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Carregando o que está liberado hoje…
                    </div>
                ) : (
                    <div className="min-w-0 space-y-4">
                        <label className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <div className="pr-3">
                                <span className="text-sm font-medium text-gray-900">
                                    Estrutura automática ligada
                                </span>
                                <p className="mt-0.5 text-xs text-gray-500">
                                    Desligada o custo é zero — o script nem baixa o pacote de
                                    preços do domínio.
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                checked={draft.active}
                                onChange={(e) => alterar({ active: e.target.checked })}
                                className="h-5 w-5 rounded border-gray-300"
                            />
                        </label>

                        {!representavel && !tocado && (
                            <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                <span>
                                    O escopo salvo tem combinações que esta tela não desenha —
                                    provavelmente foi escrito à mão. Ele continua valendo como
                                    está; só será reescrito se você marcar algo aqui.
                                    <code className="mt-1 block max-h-24 overflow-y-auto break-all font-mono text-[11px]">
                                        {JSON.stringify(salvoOriginal)}
                                    </code>
                                </span>
                            </p>
                        )}

                        {draft.active && (
                            <>
                                <div className="grid grid-cols-2 gap-2">
                                    {(['tudo', 'filtros'] as const).map((alvo) => (
                                        <button
                                            key={alvo}
                                            type="button"
                                            onClick={() => alterar({ mode: alvo })}
                                            className={`rounded-lg border p-3 text-left transition-colors ${
                                                draft.mode === alvo
                                                    ? 'border-emerald-300 bg-emerald-50'
                                                    : 'border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <span className="text-sm font-medium text-gray-900">
                                                {alvo === 'tudo'
                                                    ? 'Todo o domínio'
                                                    : 'Só onde eu escolher'}
                                            </span>
                                            <span className="mt-0.5 block text-xs text-gray-500">
                                                {alvo === 'tudo'
                                                    ? 'Qualquer país, post e bloco.'
                                                    : 'Marque abaixo o que liberar.'}
                                            </span>
                                        </button>
                                    ))}
                                </div>

                                {draft.mode === 'filtros' && (
                                    <>
                                        <p className="flex gap-2 text-xs text-gray-500">
                                            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                            Marcações de listas diferentes se cruzam: dois países
                                            e três posts liberam esses três posts nesses dois
                                            países. Lista sem nada marcado vale para todos.
                                        </p>

                                        <div className="grid min-w-0 gap-3 md:grid-cols-3">
                                            <Grupo
                                                titulo="Países"
                                                dimensao="countries"
                                                itens={opcoes.countries}
                                            />
                                            <Grupo
                                                titulo="Posts"
                                                dimensao="posts"
                                                itens={listaPosts}
                                                rotulo={(id) => (slugs[id] ? `${id} · ${slugs[id]}` : id)}
                                            />
                                            <Grupo
                                                titulo="Blocos"
                                                dimensao="blocks"
                                                itens={opcoes.blocks}
                                            />
                                        </div>
                                    </>
                                )}

                                {nadaMarcado && (
                                    <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                        Nada marcado em lista nenhuma: a estrutura fica ligada e
                                        não libera nada. Se a intenção era o domínio todo, use
                                        “Todo o domínio”.
                                    </p>
                                )}

                                {total > MUITAS_ENTRADAS && (
                                    <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                        {total} combinações vão para o ad.js, que é baixado em
                                        toda pageview. Marcar só a dimensão que importa costuma
                                        dar o mesmo resultado com muito menos.
                                    </p>
                                )}

                                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                        No ad{domainId}.js · {payload.structure_cascate.length}{' '}
                                        {payload.structure_cascate.length === 1
                                            ? 'entrada'
                                            : 'entradas'}
                                    </span>
                                    <pre className="mt-1.5 max-h-32 overflow-y-auto whitespace-pre-wrap break-all text-[11px] leading-relaxed text-gray-700">
{`activeStructureCascate: ${payload.active_structure_cascate}
structureCascate: ${formataEscopos(payload.structure_cascate)}`}
                                    </pre>
                                </div>
                            </>
                        )}

                        {/* Grudada embaixo: com muitos países a lista rola bastante,
                            e sair procurando o botão de salvar no fim é ruim. */}
                        <div className="sticky bottom-0 -mx-6 -mb-6 flex justify-end gap-2 border-t border-gray-100 bg-white px-6 py-3">
                            <Button variant="outline" onClick={() => onOpenChange(false)}>
                                Cancelar
                            </Button>
                            <Button onClick={() => salvar.mutate()} disabled={salvar.isPending}>
                                {salvar.isPending && (
                                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                                )}
                                Salvar e regerar script
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
